import type * as React from 'react';
import {
  type EmojiPickerListCategoryHeaderProps,
  type EmojiPickerListEmojiProps,
  type EmojiPickerListRowProps,
  EmojiPicker as EmojiPickerPrimitive,
} from 'frimousse';
import { LoaderIcon, SearchIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

function EmojiPicker({ className, ...props }: React.ComponentProps<typeof EmojiPickerPrimitive.Root>) {
  return (
    <EmojiPickerPrimitive.Root
      className={cn(
        'isolate flex h-full w-fit flex-col overflow-hidden rounded-md bg-ui-base text-text-base',
        className,
      )}
      // Fix emoji font rendering in Firefox on Windows (uses Twemoji by default)
      style={
        {
          '--frimousse-emoji-font': '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif',
        } as React.CSSProperties
      }
      data-slot="emoji-picker"
      data-cy="emoji-picker"
      {...props}
    />
  );
}

function EmojiPickerSearch({ className, ...props }: React.ComponentProps<typeof EmojiPickerPrimitive.Search>) {
  const tEmojiPicker = useTranslations('emojiPicker');

  return (
    <div
      className={cn('flex h-9 items-center gap-2 border-b border-b-ui-separator px-3', className)}
      data-slot="emoji-picker-search-wrapper"
    >
      <SearchIcon className="icon-4 shrink-0 opacity-50" />
      <EmojiPickerPrimitive.Search
        className="flex h-10 w-full rounded-md bg-transparent py-3 outline-hidden focus-within:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
        data-slot="emoji-picker-search"
        data-cy="emoji-picker-search"
        placeholder={tEmojiPicker('searchPlaceholder')}
        {...props}
      />
    </div>
  );
}

function EmojiPickerRow({ children, ...props }: EmojiPickerListRowProps) {
  return (
    <div {...props} className="px-1" data-slot="emoji-picker-row">
      {children}
    </div>
  );
}

function EmojiPickerEmoji({ emoji, className, ...props }: EmojiPickerListEmojiProps) {
  return (
    <button
      {...props}
      className={cn(
        'flex size-7 items-center justify-center rounded-md text-base data-active:bg-highlight-base',
        className,
      )}
      data-slot="emoji-picker-emoji"
      data-cy="emoji-picker-emoji"
    >
      {emoji.emoji}
    </button>
  );
}

function EmojiPickerCategoryHeader({ category, ...props }: EmojiPickerListCategoryHeaderProps) {
  return (
    <div
      {...props}
      className="bg-ui-base px-3 pt-3.5 pb-2 text-xs leading-none font-medium text-text-gray"
      data-slot="emoji-picker-category-header"
      data-cy="emoji-picker-category-header"
    >
      {category.label}
    </div>
  );
}

function EmojiPickerContent({ className, ...props }: React.ComponentProps<typeof EmojiPickerPrimitive.Viewport>) {
  const tEmojiPicker = useTranslations('emojiPicker');

  return (
    <EmojiPickerPrimitive.Viewport
      className={cn('outline-hidden relative flex-1', className)}
      data-slot="emoji-picker-viewport"
      data-cy="emoji-picker-viewport"
      {...props}
    >
      <EmojiPickerPrimitive.Loading
        className="absolute inset-0 flex items-center justify-center text-text-gray"
        data-slot="emoji-picker-loading"
        data-cy="emoji-picker-loading"
      >
        <LoaderIcon className="icon-4 animate-spin" />
      </EmojiPickerPrimitive.Loading>
      <EmojiPickerPrimitive.Empty
        className="absolute inset-0 flex items-center justify-center text-sm text-text-gray"
        data-slot="emoji-picker-empty"
        data-cy="emoji-picker-empty"
      >
        {tEmojiPicker('notFound')}
      </EmojiPickerPrimitive.Empty>
      <EmojiPickerPrimitive.List
        className="pb-1 select-none"
        components={{
          Row: EmojiPickerRow,
          Emoji: EmojiPickerEmoji,
          CategoryHeader: EmojiPickerCategoryHeader,
        }}
        data-slot="emoji-picker-list"
        data-cy="emoji-picker-list"
      />
    </EmojiPickerPrimitive.Viewport>
  );
}

function EmojiPickerFooter({ className, ...props }: React.ComponentProps<'div'>) {
  const tEmojiPicker = useTranslations('emojiPicker');

  return (
    <div
      className={cn(
        'max-w-(--frimousse-viewport-width) flex w-full min-w-0 items-center gap-1 border-t border-t-ui-separator p-2',
        className,
      )}
      data-slot="emoji-picker-footer"
      {...props}
    >
      <EmojiPickerPrimitive.ActiveEmoji>
        {({ emoji }) =>
          emoji ? (
            <>
              <div className="flex size-7 flex-none items-center justify-center text-lg">{emoji.emoji}</div>
              <span className="line-clamp-1 max-w-[calc(var(--frimousse-viewport-width)-50px)] text-sm text-text-base">
                {emoji.label}
              </span>
            </>
          ) : (
            <span className="ml-1.5 flex h-7 items-center truncate text-sm">{tEmojiPicker('noEmojiSelected')}</span>
          )
        }
      </EmojiPickerPrimitive.ActiveEmoji>
    </div>
  );
}

export { EmojiPicker, EmojiPickerSearch, EmojiPickerContent, EmojiPickerFooter };
